import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { getArtifact } from '../api.ts';
import type { Artifact } from '../types.ts';
import { orderedAxes } from '../lib/artifact.ts';
import { currentHashQuery, setHashQuery } from '../lib/router.ts';
import {
  DEFAULT_FILTERS,
  axisMatchesFilters,
  filtersFromParams,
  filtersToParams,
  visibleDegradedColumns,
  visibleVendors,
  type FilterState,
} from '../lib/filters.ts';
import { ComparisonTable, type SelectedCell } from '../components/ComparisonTable.tsx';
import { CellDetail } from '../components/CellDetail.tsx';
import { NotComparedList } from '../components/NotComparedList.tsx';
import { VendorExplorer } from '../components/VendorExplorer.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { DegradedNotice } from '../components/DegradedNotice.tsx';
import { CoverageStrip } from '../components/CoverageStrip.tsx';
import { AxisDemandDetail } from '../components/AxisDemandDetail.tsx';
import { DemandPageSections } from '../components/DemandSections.tsx';

/** `showCost`: false for a non-admin viewer, who never sees a dollar figure
 * (docs/specs/accounts.md, section 5). */
type Props = { showCost: boolean } & (
  | { mode: 'live'; sessionId: string; runId: string }
  | { mode: 'fixture'; artifact: Artifact }
);

/** Artifact view (docs/specs/app.md, apps/web §3, v1.1): the deliverable, filterable/pivotable. */
export function ArtifactView(props: Props): JSX.Element {
  const [artifact, setArtifact] = useState<Artifact | null>(props.mode === 'fixture' ? props.artifact : null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  // The axis whose demand detail is open (from a row-header demand key). It shares the one detail
  // slot under the matrix with the cell selection, so opening either closes the other.
  const [demandAxis, setDemandAxis] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>(() => filtersFromParams(currentHashQuery()));

  useEffect(() => {
    if (props.mode !== 'live') return;
    let cancelled = false;
    getArtifact(props.runId)
      .then((a) => {
        if (cancelled) return;
        if (!a) setError('Artifact not built yet.');
        else setArtifact(a);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [props.mode === 'live' ? props.runId : null]);

  // Filters -> URL (replaceState; see router.ts for why this doesn't loop or spam history).
  useEffect(() => {
    setHashQuery(filtersToParams(filters));
  }, [filters]);

  // URL -> filters, for back/forward navigation and pasted/shared links that already carry a
  // query. `setHashQuery` above uses replaceState, which never fires `hashchange`, so this
  // listener only reacts to genuine navigation, not its own writes.
  useEffect(() => {
    function onHashChange(): void {
      setFilters(filtersFromParams(currentHashQuery()));
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Search filtering is cheap even on the 4-vendor/125-feature stress fixture, but the search
  // box should never feel laggy while typing — defer the value the expensive per-item
  // filtering reads so keystroke handling and the controlled input stay on the fast path.
  const deferredFilters = useDeferredValue(filters);

  const axes = useMemo(() => (artifact ? orderedAxes(artifact.matrix.axes) : []), [artifact]);

  const inViewVendors = useMemo(
    () => (artifact ? visibleVendors(artifact, deferredFilters) : []),
    [artifact, deferredFilters],
  );

  // The unassessed competitors' columns (docs/specs/degraded-runs.md) — matrix-pivot only: they
  // have no record, so there is nothing for the vendor pivot to explore.
  const inViewDegraded = useMemo(
    () => (artifact ? visibleDegradedColumns(artifact, deferredFilters) : []),
    [artifact, deferredFilters],
  );

  const visibleAxes = useMemo(
    () =>
      artifact
        ? axes.filter((axis) =>
            axisMatchesFilters(artifact, axis, inViewVendors, deferredFilters, inViewDegraded),
          )
        : [],
    [artifact, axes, inViewVendors, inViewDegraded, deferredFilters],
  );

  // A selection can point at an axis/vendor the current filters just hid — drop it rather than
  // showing detail for a row the user can no longer see above it.
  useEffect(() => {
    if (!selected) return;
    const axisVisible = visibleAxes.some((a) => a.capability === selected.axis);
    const vendorVisible = inViewVendors.some((v) => v.company === selected.company);
    if (!axisVisible || !vendorVisible) setSelected(null);
  }, [selected, visibleAxes, inViewVendors]);

  // Same for the open demand detail: its row carries the key that opened it.
  useEffect(() => {
    if (demandAxis && !visibleAxes.some((a) => a.capability === demandAxis)) setDemandAxis(null);
  }, [demandAxis, visibleAxes]);

  function selectCell(cell: SelectedCell): void {
    setDemandAxis(null);
    setSelected(cell);
  }

  function toggleDemandAxis(axis: string): void {
    setSelected(null);
    setDemandAxis((current) => (current === axis ? null : axis));
  }

  if (error) return <p className="error artifact-status">{error}</p>;
  if (!artifact) return <p className="muted artifact-status">Loading…</p>;

  // Unassessed competitors count as vendors in the matrix pivot — they occupy columns there, and
  // a total that omitted them would under-report how much of this market the run actually touched.
  const degradedTotal = artifact.degraded?.failed_vendors.length ?? 0;
  const matrixShown = inViewVendors.length + inViewDegraded.length;
  const matrixTotal = artifact.vendors.length + degradedTotal;
  const resultSummary =
    filters.pivot === 'matrix'
      ? `${visibleAxes.length} of ${axes.length} capabilities · ${matrixShown} of ${matrixTotal} vendors shown`
      : `${inViewVendors.length} of ${artifact.vendors.length} vendors shown`;

  return (
    <div className="artifact-view">
      <header className="artifact-masthead">
        <div className="artifact-masthead-main">
          <span className="label">Artifact · generated {new Date(artifact.generated_at).toLocaleString()}</span>
          <h2 className="display artifact-title">{artifact.product.name}</h2>
          {artifact.product.pitch && <p className="artifact-pitch">{artifact.product.pitch}</p>}
        </div>
        {props.mode === 'live' && (
          <button className="btn sm" onClick={() => (window.location.hash = `#/sessions/${props.sessionId}`)}>
            ← Continue in chat
          </button>
        )}
      </header>

      {/* Above the coverage strip and the matrix, not below them: the reader must know the survey
          is partial BEFORE reading anything as a finding. */}
      {artifact.degraded && (
        <DegradedNotice degraded={artifact.degraded} total={artifact.run.competitors.length} />
      )}

      <CoverageStrip artifact={artifact} showCost={props.showCost} />

      <FilterBar artifact={artifact} filters={filters} onChange={setFilters} resultSummary={resultSummary} />

      {filters.pivot === 'matrix' ? (
        <>
          <section className="artifact-section">
            <div className="tick">
              <span className="label">§ Comparison matrix</span>
            </div>
            <ComparisonTable
              artifact={artifact}
              axes={visibleAxes}
              vendors={inViewVendors}
              degraded={inViewDegraded}
              filters={deferredFilters}
              selected={selected}
              onSelect={selectCell}
              selectedDemandAxis={demandAxis}
              onSelectDemand={toggleDemandAxis}
            />
          </section>

          {demandAxis && <AxisDemandDetail artifact={artifact} axis={demandAxis} highlightQuery={filters.query} />}

          {selected && <CellDetail artifact={artifact} axes={axes} selected={selected} highlightQuery={filters.query} />}

          <section className="artifact-section">
            <h3 className="tick">
              <span className="label">§ Not compared · what each vendor has that wasn't compared</span>
            </h3>
            {inViewVendors.length === 0 ? (
              <p className="panel empty-note muted">nothing matches: the vendor filter excludes every vendor</p>
            ) : (
              <div className="not-compared-grid">
                {inViewVendors.map((v) => (
                  <NotComparedList key={v.slug} vendor={v} filters={deferredFilters} />
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <section className="artifact-section">
          <div className="tick">
            <span className="label">§ By vendor</span>
          </div>
          <VendorExplorer artifact={artifact} filters={deferredFilters} />
        </section>
      )}

      {/* Outside the pivot branch: the unassigned pile, the coverage denominator and the
          "not gathered" note describe the run, not the matrix. The vendor pivot shows friction, and
          friction without where-we-looked would read as a complete account of complaints. */}
      <DemandPageSections artifact={artifact} highlightQuery={filters.query} />
    </div>
  );
}
